import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Briefcase, Inbox, ImagePlus, Pencil, MapPin, CalendarDays, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import ResourceImageUploader from "@/components/ResourceImageUploader";

const PROJECT_TYPES = [
  { value: "shooting", label: "Shooting" },
  { value: "casting", label: "Casting" },
  { value: "mode", label: "Mode" },
  { value: "beaute", label: "Beauté" },
  { value: "autre", label: "Autre" },
];

const EMPTY = {
  title: "", description: "", project_type: "shooting", location: "",
  paid: false, date: "", status: "active", image_caption: "",
  is_premium_only: false, deadline: "",
};

export default function ProCollaborationsPage() {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [collabs, setCollabs] = useState([]);
  const [chatSummary, setChatSummary] = useState({}); // {cid: {unread_count, last_message}}
  const [open, setOpen] = useState(searchParams.get("new") === "1");
  const [step, setStep] = useState("form"); // form | images
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (!loading && user && !user.role) navigate("/onboarding");
    if (!loading && user && (user.role !== "creator" && user.role !== "professional")) {
      if (user.role === "talent") navigate("/talent");
      else if (user.role === "admin") navigate("/admin");
    }
  }, [user, loading, navigate]);

  useEffect(() => { if ((user?.role === "creator" || user?.role === "professional")) load(); }, [user]);

  const load = async () => {
    const { data } = await api.get("/my/collaborations");
    setCollabs(data);
    // Enrich with chat metadata (unread, last_message). Fail-silent so the page works without chat.
    try {
      const { data: sum } = await api.get("/my/collaborations/summary");
      const map = {};
      (sum.items || []).forEach((it) => { map[it.collaboration.id] = it; });
      setChatSummary(map);
    } catch { /* ignore */ }
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setStep("form"); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      title: c.title || "", description: c.description || "",
      project_type: c.project_type || "shooting",
      location: c.location || "", paid: !!c.paid,
      date: (c.date || "").slice(0, 10), status: c.status || "active",
      image_caption: c.image_caption || "",
      is_premium_only: !!c.is_premium_only,
      deadline: (c.deadline || "").slice(0, 10),
    });
    setStep("form");
    setOpen(true);
  };

  const saveForm = async () => {
    if (!form.title.trim()) { toast.error("Titre requis"); return; }
    try {
      const payload = { ...form, date: form.date || null };
      let savedId;
      if (editing) {
        await api.put(`/collaborations/${editing.id}`, payload);
        savedId = editing.id;
        toast.success("Collaboration mise à jour");
      } else {
        const { data } = await api.post("/collaborations", payload);
        savedId = data.id;
        toast.success("Collaboration créée");
      }
      await load();
      // Move to images step (after load to get fresh data with images)
      const fresh = (await api.get("/my/collaborations")).data.find((c) => c.id === savedId);
      setEditing(fresh);
      setStep("images");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleStatusChange = async (id, status) => {
    await api.put(`/collaborations/${id}`, { status });
    await load();
  };

  const onImagesChange = async () => {
    await load();
    const fresh = (await api.get("/my/collaborations")).data.find((c) => c.id === editing.id);
    setEditing(fresh);
  };

  if (!user || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFBF5]" data-testid="pro-loading">
        <div className="w-10 h-10 border-2 border-[#C68E59] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AppShell>
      <div data-testid="pro-collaborations-page">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-10">
          <div>
            <p className="caption-label text-[#A26F3E] mb-2">Gestion</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A]">Mes collaborations</h1>
            <p className="text-sm text-[#666] mt-2">Publiez, gérez et illustrez vos projets.</p>
          </div>
          <Button onClick={openNew} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="new-collab-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle collaboration
          </Button>
        </div>

        {!profile.verified && (
          <div className="bg-[#FAF3EA] border border-[#E6C29A] rounded-2xl p-6 mb-8 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-[#8B5A2B]" />
            </div>
            <div>
              <p className="font-semibold text-[#1A1A1A]">Profil en attente de vérification</p>
              <p className="text-sm text-[#6E4B2A]">Votre profil sera vérifié par notre équipe pour obtenir le badge Vérifié.</p>
            </div>
          </div>
        )}

        {collabs.length === 0 && (
          <div className="bg-white rounded-3xl border border-[#EADDC9] p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-[#F5E6D0] flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-6 h-6 text-[#C68E59]" />
            </div>
            <p className="font-serif text-xl text-[#1A1A1A] mb-2">Aucune collaboration publiée</p>
            <p className="text-sm text-[#8B8680] mb-6">Publiez votre première collaboration pour attirer les talents.</p>
            <Button onClick={openNew} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white">
              <Plus className="w-4 h-4 mr-2" /> Publier une collaboration
            </Button>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {collabs.map((c) => (
            <CollabCard
              key={c.id}
              c={c}
              chatMeta={chatSummary[c.id]}
              onEdit={openEdit}
              onStatusChange={handleStatusChange}
            />
          ))}
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {step === "form"
                ? (editing ? "Modifier la collaboration" : "Nouvelle collaboration")
                : "Images de la collaboration"}
            </DialogTitle>
          </DialogHeader>

          {step === "form" && (
            <div className="space-y-4 py-2">
              <div>
                <Label className="caption-label">Titre *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl mt-2" data-testid="collab-title-input" />
              </div>
              <div>
                <Label className="caption-label">Description</Label>
                <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-2" data-testid="collab-desc-input" />
              </div>
              <div>
                <Label className="caption-label">Légende visuelle</Label>
                <Input value={form.image_caption} onChange={(e) => setForm({ ...form, image_caption: e.target.value })} className="rounded-xl mt-2" placeholder="Une courte phrase qui évoque l'ambiance des images…" data-testid="collab-caption-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="caption-label">Type de projet</Label>
                  <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
                    <SelectTrigger className="rounded-xl mt-2" data-testid="collab-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{PROJECT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="caption-label">Localisation</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-xl mt-2" data-testid="collab-location-input" />
                </div>
                <div>
                  <Label className="caption-label">Date (optionnel)</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl mt-2" />
                </div>
                <div>
                  <Label className="caption-label">Statut</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="rounded-xl mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Brouillon</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="closed">Fermée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-3 pt-1 flex-wrap">
                <div className="flex items-center gap-3">
                  <Switch checked={form.paid} onCheckedChange={(v) => setForm({ ...form, paid: v })} data-testid="collab-paid-switch" />
                  <Label className="caption-label m-0">Rémunérée</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={form.is_premium_only} onCheckedChange={(v) => setForm({ ...form, is_premium_only: v })} data-testid="collab-premium-only-switch" />
                  <Label className="caption-label m-0">Réservée aux talents Premium</Label>
                </div>
                <div className="flex-1 min-w-[180px]">
                  <Label className="caption-label">Date limite candidature</Label>
                  <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="rounded-xl mt-2" data-testid="collab-deadline" />
                </div>
              </div>
            </div>
          )}

          {step === "images" && editing && (
            <div className="py-2">
              <p className="text-sm text-[#6E4B2A] mb-5">
                Ajoutez jusqu'à 5 images (1 principale + 4 secondaires). Les images sont automatiquement compressées.
              </p>
              <ResourceImageUploader
                resourceType="collaborations"
                resourceId={editing.id}
                coverImage={editing.cover_image}
                galleryImages={editing.gallery_images || []}
                max={{ gallery: 4 }}
                onChange={onImagesChange}
              />
            </div>
          )}

          <DialogFooter>
            {step === "form" ? (
              <>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Annuler</Button>
                <Button onClick={saveForm} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="collab-save-submit">
                  {editing ? "Enregistrer" : "Créer & ajouter des images"}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setStep("form")} className="rounded-full">Retour</Button>
                <Button onClick={() => setOpen(false)} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="collab-images-done-btn">
                  Terminer
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function CollabCard({ c, chatMeta, onEdit, onStatusChange }) {
  const cover = c.cover_image ? `${API}/files/${c.cover_image}` : null;
  const unread = chatMeta?.unread_count || 0;
  const last = chatMeta?.last_message;
  const participants = chatMeta?.participants || [];
  return (
    <div className="bg-white rounded-3xl border border-[#EADDC9] overflow-hidden group" data-testid={`my-collab-${c.id}`}>
      <div className="relative aspect-[16/10] bg-[#FAF3EA] overflow-hidden">
        {cover ? (
          <img src={cover} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-[#B3A78C]">
            <ImagePlus className="w-8 h-8 mb-2" />
            <span className="text-xs">Aucune image</span>
          </div>
        )}
        {c.paid && (
          <span className="absolute top-3 right-3 px-3 py-0.5 rounded-full bg-[#E7F0E3] text-[#3F6A39] text-[10px] tracking-wide uppercase border border-[#BFD4B7]">
            Rémunéré
          </span>
        )}
        <span className="absolute top-3 left-3 px-3 py-0.5 rounded-full bg-white/85 backdrop-blur text-[#1A1A1A] text-[10px] tracking-widest uppercase">
          {c.project_type}
        </span>
      </div>
      <div className="p-5">
        <h3 className="font-serif text-xl text-[#1A1A1A] leading-tight">{c.title}</h3>
        <p className="text-xs text-[#8C7D6B] mt-1 flex items-center gap-3">
          {c.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{c.location}</span>}
          {c.date && <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" />{new Date(c.date).toLocaleDateString("fr-FR")}</span>}
        </p>
        {c.image_caption && <p className="text-xs italic text-[#6E4B2A] mt-3 line-clamp-2">« {c.image_caption} »</p>}

        {/* Chat preview */}
        {(last || participants.length > 1) && (
          <Link
            to={`/collaborations/${c.id}?tab=discussion`}
            className="mt-4 flex items-center gap-3 rounded-2xl border border-[#EADDC9] bg-[#FAF3EA]/40 hover:bg-[#FAF3EA] px-3 py-2.5 transition-colors group/chat"
            data-testid={`collab-chat-preview-${c.id}`}
          >
            <div className="relative flex-shrink-0">
              <div className="flex -space-x-2">
                {participants.slice(0, 3).map((p) => (
                  <ParticipantBubble key={p.user_id} user={p} size={28} />
                ))}
              </div>
              {unread > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C68E59] text-white text-[10px] font-semibold flex items-center justify-center" data-testid={`collab-unread-${c.id}`}>
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              {last ? (
                <>
                  <p className={`text-xs truncate ${unread > 0 ? "text-[#1A1A1A] font-medium" : "text-[#6E4B2A]"}`}>
                    {last.body || "📎 Pièce jointe"}
                  </p>
                  <p className="text-[10px] text-[#A26F3E] mt-0.5">
                    {new Date(last.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                    {" · "}
                    {new Date(last.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </>
              ) : (
                <p className="text-xs text-[#A26F3E]">Démarrer la discussion</p>
              )}
            </div>
            <MessageCircle className="w-4 h-4 text-[#A26F3E] group-hover/chat:text-[#1A1A1A] transition-colors" />
          </Link>
        )}

        <div className="mt-4 flex items-center gap-2">
          <Select value={c.status} onValueChange={(v) => onStatusChange(c.id, v)}>
            <SelectTrigger className="rounded-full h-9 text-xs" data-testid={`collab-status-${c.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Brouillon</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="closed">Fermée</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="rounded-full border-[#EADDC9]" onClick={() => onEdit(c)} data-testid={`edit-collab-${c.id}`}>
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Link to={`/createurs/collaborations/${c.id}/applications`} className="ml-auto">
            <Button size="sm" variant="outline" className="rounded-full border-[#EADDC9]" data-testid={`view-apps-${c.id}`}>
              <Inbox className="w-3.5 h-3.5 mr-1" /> {c.application_count || 0}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function ParticipantBubble({ user, size = 28 }) {
  const initials = `${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase() || "?";
  const style = { width: size, height: size };
  if (user?.avatar_url) {
    return <img src={user.avatar_url} alt="" className="rounded-full object-cover ring-2 ring-white" style={style} />;
  }
  return (
    <div
      className="rounded-full bg-gradient-to-br from-[#E6C29A] to-[#A26F3E] text-white text-[10px] font-medium flex items-center justify-center ring-2 ring-white"
      style={style}
    >
      {initials}
    </div>
  );
}
